import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Plus, Edit, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const formVacio = () => ({ nombre: "", concentracion: "", marca: "", descripcion: "", nombre_erp: "" });

export default function Medicamentos() {
  const [meds, setMeds] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(formVacio());

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/medicamentos`);
      setMeds(data);
      setFiltered(data);
    } catch { toast.error("Error al cargar medicamentos"); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(meds.filter(m =>
      m.nombre.toLowerCase().includes(q) ||
      (m.concentracion || "").toLowerCase().includes(q) ||
      (m.marca || "").toLowerCase().includes(q)
    ));
  }, [search, meds]);

  const set = (f) => (e) => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      if (editing) {
        await axios.put(`${API}/medicamentos/${editing.id}`, payload);
        toast.success("Medicamento actualizado");
      } else {
        await axios.post(`${API}/medicamentos`, payload);
        toast.success("Medicamento creado");
      }
      setOpen(false); reset(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (m) => {
    setEditing(m);
    setForm({ nombre: m.nombre || "", concentracion: m.concentracion || "", marca: m.marca || "", descripcion: m.descripcion || "", nombre_erp: m.nombre_erp || "" });
    setOpen(true);
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar ${nombre}?`)) return;
    try {
      await axios.delete(`${API}/medicamentos/${id}`);
      toast.success("Medicamento eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const reset = () => { setForm(formVacio()); setEditing(null); };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Medicamentos</h1>
          <p className="text-sm text-slate-500">Catálogo de medicamentos crónicos del programa</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button className="bg-red-700 hover:bg-red-800 text-white">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Medicamento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar Medicamento" : "Nuevo Medicamento"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label>Nombre *</Label>
                <Input value={form.nombre} onChange={set("nombre")} placeholder="Ej: Metformina" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Concentración</Label>
                  <Input value={form.concentracion} onChange={set("concentracion")} placeholder="Ej: 850mg" />
                </div>
                <div>
                  <Label>Marca</Label>
                  <Input value={form.marca} onChange={set("marca")} placeholder="Ej: Glucophage" />
                </div>
              </div>
              <div>
                <Label>Nombre en el ERP</Label>
                <Input
                  value={form.nombre_erp}
                  onChange={set("nombre_erp")}
                  placeholder="Nombre exacto como aparece en el sistema de ventas"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Si está vacío, se usa el nombre de arriba para detectar compras en el ERP.
                </p>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input value={form.descripcion} onChange={set("descripcion")} />
              </div>
              <Button type="submit" className="w-full bg-red-700 hover:bg-red-800">
                {editing ? "Actualizar" : "Crear"} Medicamento
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input placeholder="Buscar medicamento..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Concentración</TableHead>
                <TableHead>Marca</TableHead>
                <TableHead>Nombre en ERP</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-slate-400">
                    No hay medicamentos. Agrega los medicamentos crónicos del programa.
                  </TableCell>
                </TableRow>
              ) : filtered.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="font-medium">{m.nombre}</TableCell>
                  <TableCell className="text-slate-500">{m.concentracion || "-"}</TableCell>
                  <TableCell className="text-slate-500">{m.marca || "-"}</TableCell>
                  <TableCell>
                    {m.nombre_erp
                      ? <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{m.nombre_erp}</span>
                      : <span className="text-xs text-slate-300">igual al nombre</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(m)} className="text-blue-600 hover:bg-blue-50 h-8 w-8 p-0">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id, m.nombre)} className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
